import { useRef, useState } from 'react'
import Button from '../../button'
import FlightIcon from '../../flight-icon'
import onClickHandler, { type HandlerArgs } from '../clipboard'
import classNames from 'classnames'

export interface CopyButtonProps {
	size?: 'small' | 'medium'
	isIconOnly?: boolean
	isFullWidth?: boolean
	text: string
	textToCopy?: string
	getTextFn?: () => string
	targetToCopy?: string | Element
	onSuccess?: (args: HandlerArgs) => void
	onError?: (args: HandlerArgs) => void
	className?: string
}

const DEFAULT_ICON = 'clipboard-copy'
const SUCCESS_ICON = 'clipboard-checked'
const ERROR_ICON = 'clipboard-x'
const DEFAULT_STATUS = 'idle'

export default function CopyButton({
	size = 'medium',
	isIconOnly = false,
	isFullWidth = false,
	text,
	textToCopy,
	getTextFn,
	targetToCopy,
	onSuccess,
	onError,
	className,
	...rest
}: CopyButtonProps) {
	const [status, setStatus] = useState(DEFAULT_STATUS)
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const icon =
		status === 'idle'
			? DEFAULT_ICON
			: status === 'success'
			? SUCCESS_ICON
			: ERROR_ICON

	const resetStatusDelayed = () => {
		if (timer.current) {
			clearTimeout(timer.current)
		}
		timer.current = setTimeout(() => {
			setStatus(DEFAULT_STATUS)
		}, 1500)
	}

	const _onSuccess = (args: HandlerArgs) => {
		setStatus('success')
		resetStatusDelayed()

		if (onSuccess) {
			onSuccess(args)
		}
	}

	const _onError = (args: HandlerArgs) => {
		setStatus('error')
		resetStatusDelayed()

		if (onError) {
			onError(args)
		}
	}

	return (
		<Button
			className={classNames(
				'hds-copy-button',
				`hds-button--size-${size}`,
				`hds-copy-button--status-${status}`,
				className
			)}
			size={size}
			isFullWidth={isFullWidth}
			text={text}
			icon={<FlightIcon name={icon} stretched />}
			isIconOnly={isIconOnly}
			color="secondary"
			iconPosition="trailing"
			{...onClickHandler({
				text: textToCopy,
				getTextFn: getTextFn,
				target: targetToCopy,
				onSuccess: _onSuccess,
				onError: _onError,
			})}
			{...rest}
		/>
	)
}
